import type { ItemType, PullPage, SyncItem } from '../api/types'

/**
 * The pulled items of one type as this tab holds them: the newest version of each id, tombstones
 * included so an older copy can never bring a deleted item back, and the type's high-water mark.
 */
export interface ItemCache {
  items: SyncItem[]
  /** Highest `updatedAt` the server has returned for this type; 0 before the first pull. */
  since: number
}

/** Each pull re-reads this much before the high-water mark, because device clocks drift (sync contract). */
export const PULL_OVERLAP_MS = 24 * 60 * 60 * 1000

export const EMPTY_CACHE: ItemCache = { items: [], since: 0 }

/** Where the next pull for a type starts: 24 hours before its high-water mark, or 0 for a full read. */
export function pullSince(cache: ItemCache | undefined): number {
  if (!cache || cache.since <= 0) return 0
  return Math.max(0, cache.since - PULL_OVERLAP_MS)
}

/**
 * Applies [incoming] items: an item replaces the cached copy only when it is strictly newer (last write
 * wins). Returns the same cache object when nothing changed, so React Query keeps its references.
 */
export function mergeItems(cache: ItemCache, incoming: readonly SyncItem[]): ItemCache {
  if (incoming.length === 0) return cache
  const byId = new Map<string, SyncItem>()
  for (const item of cache.items) byId.set(item.id, item)
  let changed = false
  for (const item of incoming) {
    const existing = byId.get(item.id)
    if (existing && existing.updatedAt >= item.updatedAt) continue
    byId.set(item.id, item)
    changed = true
  }
  return changed ? { items: [...byId.values()], since: cache.since } : cache
}

/** Merges a pull result and moves the high-water mark forward. */
export function applyPull(cache: ItemCache, pulled: Pick<PullPage, 'items' | 'since'>): ItemCache {
  const merged = mergeItems(cache, pulled.items)
  const since = Math.max(cache.since, pulled.since)
  return since === merged.since ? merged : { items: merged.items, since }
}

/** Groups items by type, keeping their order. */
export function groupByType<T extends Pick<SyncItem, 'type'>>(items: readonly T[]): Map<ItemType, T[]> {
  const groups = new Map<ItemType, T[]>()
  for (const item of items) {
    const group = groups.get(item.type)
    if (group) group.push(item)
    else groups.set(item.type, [item])
  }
  return groups
}

/**
 * The newest version of each item of [type] among [items], which may hold several versions of one id
 * (raw pulls, imports). Readers build on this.
 */
export function latestById(items: readonly SyncItem[], type: ItemType): SyncItem[] {
  const latest = new Map<string, SyncItem>()
  for (const item of items) {
    if (item.type !== type) continue
    const existing = latest.get(item.id)
    if (!existing || existing.updatedAt < item.updatedAt) latest.set(item.id, item)
  }
  return [...latest.values()]
}
